<!-- NOTE: slot text should be wrapped in a <span> to avoid default styles (e.g. <p>) -->
<script setup lang="ts">
import { onKeyStroke } from '@vueuse/core';
import { onMounted, onUnmounted, ref } from 'vue';

import {
  blockBackgroundScroll,
  enableBackgroundScroll,
} from '../../helpers/blockBackgroundScroll';
import { KeyboardKeyCode } from '../../models';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import AppOverlay from '../modals/AppOverlay.vue';
import { AppSlideUpAndDownTransition } from '../transitions';

interface AppPanelEmits {
  (eventName: 'close'): void;
}

const emit = defineEmits<AppPanelEmits>();

const drawerRef = ref<HTMLDivElement | null>(null);
const isOpen = ref(false);

function handleClose(): void {
  isOpen.value = false;
}

function onTransitionComplete(): void {
  if (!isOpen.value) {
    emit('close');
  }
}

onKeyStroke(KeyboardKeyCode.Escape, () => handleClose());

onMounted(() => {
  blockBackgroundScroll();
  isOpen.value = true;
});

onUnmounted(() => {
  isOpen.value = false;
  enableBackgroundScroll();
});
</script>

<template>
  <AppOverlay :is-modal="true" @close="handleClose">
    <AppSlideUpAndDownTransition @after-leave="onTransitionComplete">
      <div v-if="isOpen" ref="drawerRef" class="app-drawer">
        <div class="header">
          <slot name="header" />
          <AppSvgIcon class="close-icon" name="close" @click="handleClose" />
        </div>
        <div class="content">
          <slot name="content" />
        </div>
      </div>
    </AppSlideUpAndDownTransition>
  </AppOverlay>
</template>

<style lang="scss" scoped>
.app-drawer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  width: 100%;
  height: 99dvh;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  z-index: var(--modal-z-index);
  background-color: var(--color-white);
  border-radius: var(--border-radius-20) var(--border-radius-20) 0 0;
  overflow: hidden;
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-16);
    border-bottom: 1px solid var(--color-scrollbar);
    .close-icon {
      cursor: pointer;
    }
  }
  .content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
}
</style>
